import React, { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import { formatDate } from '../lib/formatUtils';
import { PersonAvatar } from '../components/PersonAvatar';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Switch } from '../components/ui/switch';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { TruncatedCell } from '../components/common/TruncatedCell';
import { CalendarDays, Check, X, Loader2, Play, Users, TrendingUp, AlertTriangle, Clock, History, Ban, FileSpreadsheet, Mail } from 'lucide-react';
import { EmployeeLink } from '../components/links/EntityLinks';
import { toast } from 'sonner';
import { exportToExcel } from '../lib/exportUtils';
import { usePageSize } from '../context/PageSizeContext';
import { Pagination } from '../components/Pagination';

const statusColors = {
  pending: 'bg-yellow-500/10 text-yellow-500 border-yellow-500/20',
  approved: 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20',
  rejected: 'bg-red-500/10 text-red-500 border-red-500/20',
  cancelled: 'bg-zinc-500/10 text-zinc-500 border-zinc-500/20',
};

export const LeavesPage = () => {
  const { pageSize } = usePageSize();
  const [tab, setTab] = useState('requests');
  const [leaves, setLeaves] = useState([]);
  const [totalRequests, setTotalRequests] = useState(0);
  const [page, setPage] = useState(1);
  // Status-bucketed counts (server-side aggregate) for the stat cards.
  const [stats, setStats] = useState({ total: 0, pending: 0, approved: 0, rejected: 0, cancelled: 0 });
  const [balances, setBalances] = useState([]);
  const [auditLog, setAuditLog] = useState([]);
  const [auditTotal, setAuditTotal] = useState(0);
  const [auditPage, setAuditPage] = useState(1);
  const [exporting, setExporting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [rejectDialog, setRejectDialog] = useState({ open: false, id: null });
  const [rejectReason, setRejectReason] = useState('');
  // Approve dialog — lets the approver split a request into paid + unpaid days.
  // Pre-fills paid = min(days, current_balance), unpaid = days - paid so the
  // common case is one click.
  const [approveDialog, setApproveDialog] = useState({ open: false, leave: null, balance: null });
  const [approvePaid, setApprovePaid] = useState('0');
  const [approveUnpaid, setApproveUnpaid] = useState('0');
  const [approveComment, setApproveComment] = useState('');
  // Admin / HR cancel dialog — overrides employee self-cancel rules.
  const [adminCancelDialog, setAdminCancelDialog] = useState({ open: false, leave: null });
  const [adminCancelReason, setAdminCancelReason] = useState('');
  const [adminCancelRestore, setAdminCancelRestore] = useState(true);
  const [adminCancelNotify, setAdminCancelNotify] = useState(false);
  const [adminCancelBusy, setAdminCancelBusy] = useState(false);
  const [accrualDialog, setAccrualDialog] = useState(false);
  const [accrualLoading, setAccrualLoading] = useState(false);

  const fetchLeaves = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), limit: String(pageSize) });
      if (statusFilter && statusFilter !== 'all') params.set('status', statusFilter);
      const res = await api.get(`/leave-mgmt/requests?${params.toString()}`);
      if (res.data.success) {
        setLeaves(res.data.data);
        setTotalRequests(res.data.total || 0);
      }
    } catch {} finally { setLoading(false); }
  }, [statusFilter, page, pageSize]);

  // Server-side aggregate counts — independent of current page / filter.
  const fetchStats = useCallback(async () => {
    try {
      const res = await api.get('/leave-mgmt/stats');
      if (res.data.success) setStats(res.data.data);
    } catch {}
  }, []);

  const fetchBalances = useCallback(async () => {
    try {
      const res = await api.get('/leave-mgmt/all-balances');
      if (res.data.success) setBalances(res.data.data);
    } catch {}
  }, []);

  const fetchAudit = useCallback(async () => {
    try {
      const res = await api.get(`/leave-mgmt/audit-log?page=${auditPage}&limit=${pageSize}`);
      if (res.data.success) {
        setAuditLog(res.data.data);
        setAuditTotal(res.data.total || 0);
      }
    } catch {}
  }, [auditPage, pageSize]);

  // Reset to page 1 when status filter changes
  useEffect(() => { setPage(1); }, [statusFilter]);

  useEffect(() => { fetchLeaves(); fetchStats(); }, [fetchLeaves, fetchStats]);
  useEffect(() => { if (tab === 'balances') fetchBalances(); if (tab === 'audit') fetchAudit(); }, [tab, fetchBalances, fetchAudit]);

  const openApproveDialog = async (leave) => {
    // Look up the employee's current balance for this leave type so we can
    // sensibly default the paid / unpaid split AND surface accrual numbers in
    // the dialog for quick reference.
    let avail = 0;
    let balanceSnapshot = null;
    try {
      const res = await api.get(`/leave-mgmt/balance/${leave.employee_id}`);
      if (res.data.success) {
        const b = res.data.data || {};
        avail = (b.total_accrued || 0) + (b.carry_forward_balance || 0) - (b.total_used || 0);
        balanceSnapshot = {
          accrued: +(b.total_accrued || 0).toFixed(2),
          carry_forward: +(b.carry_forward_balance || 0).toFixed(2),
          used: +(b.total_used || 0).toFixed(2),
          available: +avail.toFixed(2),
          fy: b.fy || '',
        };
      }
    } catch { /* no balance — default to 0 paid, all unpaid */ }
    const days = Number(leave.days) || 0;
    const paid = Math.max(0, Math.min(days, Math.floor(avail * 2) / 2));   // half-day step
    const unpaid = Math.max(0, +(days - paid).toFixed(2));
    setApprovePaid(String(paid));
    setApproveUnpaid(String(unpaid));
    setApproveComment('');
    setApproveDialog({ open: true, leave, balance: balanceSnapshot });
  };

  const handleApprove = async () => {
    const { leave } = approveDialog;
    if (!leave) return;
    const paid = parseFloat(approvePaid) || 0;
    const unpaid = parseFloat(approveUnpaid) || 0;
    const days = Number(leave.days) || 0;
    if (Math.abs(paid + unpaid - days) > 0.01) {
      toast.error(`Paid + Unpaid must equal ${days}`);
      return;
    }
    if (paid < 0 || unpaid < 0) {
      toast.error('Days must be non-negative');
      return;
    }
    // Mirror the backend hard validation so the user gets instant feedback.
    const avail = approveDialog.balance?.available;
    if (typeof avail === 'number' && paid > avail + 0.01) {
      toast.error(`Paid days (${paid}) exceeds available balance (${avail}). Move the surplus into unpaid.`);
      return;
    }
    try {
      await api.post(`/leave-mgmt/${leave.id}/approve`, {
        paid_days: paid,
        unpaid_days: unpaid,
        comment: approveComment,
      });
      toast.success('Leave approved');
      setApproveDialog({ open: false, leave: null, balance: null });
      fetchLeaves();
      fetchStats();
      fetchBalances();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleReject = async () => {
    try {
      await api.post(`/leave-mgmt/${rejectDialog.id}/reject`, { reason: rejectReason });
      toast.success('Leave rejected');
      setRejectDialog({ open: false, id: null });
      setRejectReason('');
      fetchLeaves();
      fetchStats();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const openAdminCancelDialog = (leave) => {
    setAdminCancelReason('');
    setAdminCancelRestore(true);
    setAdminCancelNotify(false);
    setAdminCancelDialog({ open: true, leave });
  };

  const handleAdminCancel = async () => {
    const { leave } = adminCancelDialog;
    if (!leave) return;
    if (adminCancelReason.trim().length < 10) {
      toast.error('Reason must be at least 10 characters');
      return;
    }
    setAdminCancelBusy(true);
    try {
      const res = await api.post(`/leave-mgmt/${leave.id}/admin-cancel`, {
        reason: adminCancelReason.trim(),
        restore_balance: adminCancelRestore,
        notify_employee: adminCancelNotify,
      });
      const restored = res.data?.restored_days || 0;
      const emailed = res.data?.email_sent;
      const restoreMsg = restored > 0 ? ` ${restored} day(s) restored to balance.` : '';
      const emailMsg = emailed ? ' Employee was notified by email.' : ' Silent — no email sent.';
      toast.success('Leave cancelled.' + restoreMsg + emailMsg);
      setAdminCancelDialog({ open: false, leave: null });
      setAdminCancelReason('');
      fetchLeaves();
      fetchStats();
      fetchBalances();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to cancel');
    } finally {
      setAdminCancelBusy(false);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams({ limit: '10000' });
      if (statusFilter && statusFilter !== 'all') params.set('status', statusFilter);
      const res = await api.get(`/leave-mgmt/requests?${params.toString()}`);
      const rows = res.data?.data || [];
      if (!rows.length) { toast.info('No leave requests to export'); return; }
      exportToExcel(rows.map(l => ({
        employee_name: l.employee_name,
        start_date: l.start_date,
        end_date: l.end_date,
        days: l.days,
        lop_days: l.lop_days || 0,
        leave_type: l.leave_type || '',
        status: l.status,
        reason: l.reason || '',
        applied_on: l.applied_on || l.created_at,
        approved_by: l.approved_by_name || '',
      })), [
        { key: 'employee_name', header: 'Employee' },
        { key: 'start_date', header: 'From' },
        { key: 'end_date', header: 'To' },
        { key: 'days', header: 'Days' },
        { key: 'lop_days', header: 'LOP' },
        { key: 'leave_type', header: 'Type' },
        { key: 'status', header: 'Status' },
        { key: 'reason', header: 'Reason' },
        { key: 'applied_on', header: 'Applied On' },
        { key: 'approved_by', header: 'Approved By' },
      ], 'Leave_Requests');
      toast.success(`Exported ${rows.length} requests`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Export failed');
    } finally { setExporting(false); }
  };

  const handleRunAccrual = async () => {
    setAccrualLoading(true);
    try {
      const res = await api.post('/leave-mgmt/accrue', {});
      toast.success(res.data.message);
      setAccrualDialog(false);
      fetchBalances();
    } catch (e) { toast.error(e.response?.data?.detail || 'Accrual failed'); }
    finally { setAccrualLoading(false); }
  };

  const pendingCount = stats.pending;
  const approvedCount = stats.approved;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="leaves-page">
      <div className="flex flex-col sm:flex-row justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Leave Management</h1>
          <p className="text-muted-foreground">Manage earned leave requests, balances and accruals</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={handleExport} disabled={exporting} data-testid="leaves-export-btn">
            {exporting ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <FileSpreadsheet className="h-4 w-4 mr-1" />}
            Excel
          </Button>
          <Button onClick={() => setAccrualDialog(true)} data-testid="run-accrual-btn">
            <Play className="h-4 w-4 mr-2" />Run Accrual
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="stat-card"><CardContent className="p-4"><div className="flex items-center gap-3"><div className="h-10 w-10 rounded-full bg-yellow-500/10 flex items-center justify-center"><Clock className="h-5 w-5 text-yellow-500" /></div><div><p className="text-2xl font-bold" data-testid="pending-count">{pendingCount}</p><p className="text-xs text-muted-foreground">Pending</p></div></div></CardContent></Card>
        <Card className="stat-card"><CardContent className="p-4"><div className="flex items-center gap-3"><div className="h-10 w-10 rounded-full bg-emerald-500/10 flex items-center justify-center"><Check className="h-5 w-5 text-emerald-500" /></div><div><p className="text-2xl font-bold" data-testid="approved-count">{approvedCount}</p><p className="text-xs text-muted-foreground">Approved</p></div></div></CardContent></Card>
        <Card className="stat-card"><CardContent className="p-4"><div className="flex items-center gap-3"><div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center"><Users className="h-5 w-5 text-primary" /></div><div><p className="text-2xl font-bold">{balances.length}</p><p className="text-xs text-muted-foreground">Employees</p></div></div></CardContent></Card>
        <Card className="stat-card"><CardContent className="p-4"><div className="flex items-center gap-3"><div className="h-10 w-10 rounded-full bg-blue-500/10 flex items-center justify-center"><TrendingUp className="h-5 w-5 text-blue-500" /></div><div><p className="text-2xl font-bold" data-testid="leaves-total-count">{stats.total}</p><p className="text-xs text-muted-foreground">Total Requests</p></div></div></CardContent></Card>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="requests" data-testid="tab-requests">Requests {pendingCount > 0 && <Badge className="ml-1.5 h-5 px-1.5 text-xs bg-yellow-500 text-white">{pendingCount}</Badge>}</TabsTrigger>
          <TabsTrigger value="balances" data-testid="tab-balances">Balances</TabsTrigger>
          <TabsTrigger value="audit" data-testid="tab-audit">Audit Log</TabsTrigger>
        </TabsList>

        <TabsContent value="requests" className="space-y-4">
          <div className="flex gap-2">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[180px]" data-testid="leave-status-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {loading ? (
            <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>
          ) : leaves.length === 0 ? (
            <Card><CardContent className="flex flex-col items-center py-16 text-muted-foreground"><CalendarDays className="h-12 w-12 mb-4 opacity-40" /><p>No leave requests found</p></CardContent></Card>
          ) : (
            <Card>
              <Table>
                <TableHeader><TableRow>
                  <TableHead>Employee</TableHead>
                  <TableHead>Dates</TableHead>
                  <TableHead>Days</TableHead>
                  <TableHead>LOP</TableHead>
                  <TableHead>Reason</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Applied</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {leaves.map(leave => (
                    <TableRow key={leave.id} data-testid={`leave-row-${leave.id}`}>
                      <TableCell><div className="flex items-center gap-2"><PersonAvatar firstName={(leave.employee_name || '').split(' ')[0]} lastName={(leave.employee_name || '').split(' ').slice(1).join(' ')} picture={leave.employee_profile_picture} seed={leave.employee_id} size="sm" /><span className="font-medium"><EmployeeLink id={leave.employee_id} name={leave.employee_name} /></span></div></TableCell>
                      <TableCell className="text-sm">
                        {formatDate(leave.start_date)}{leave.start_date !== leave.end_date && ` - ${formatDate(leave.end_date)}`}
                        {leave.is_half_day && <Badge variant="outline" className="ml-1 text-xs">Half</Badge>}
                      </TableCell>
                      <TableCell>{leave.days}</TableCell>
                      <TableCell>{leave.lop_days > 0 ? <span className="text-red-400">{leave.lop_days}</span> : '-'}</TableCell>
                      <TableCell className="max-w-[260px] text-sm">
                        <TruncatedCell
                          text={leave.reason}
                          maxWidth={250}
                          testId={`leave-reason-${leave.id}`}
                        />
                      </TableCell>
                      <TableCell>
                        {leave.status === 'cancelled' && leave.cancelled_by === 'admin' ? (
                          <TooltipProvider delayDuration={150}>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span className="inline-flex items-center gap-1 cursor-help" data-testid={`admin-cancelled-badge-${leave.id}`}>
                                  <Badge className={statusColors[leave.status]}>{leave.status}</Badge>
                                  <Badge variant="outline" className="text-[10px] border-amber-500/40 text-amber-500 px-1.5 py-0">
                                    <Ban className="h-2.5 w-2.5 mr-0.5" />HR
                                  </Badge>
                                </span>
                              </TooltipTrigger>
                              <TooltipContent
                                side="top"
                                className="max-w-xs bg-background text-foreground border border-border shadow-lg p-3 space-y-1.5 text-xs"
                                data-testid={`admin-cancel-tooltip-${leave.id}`}
                              >
                                <div className="font-semibold text-amber-500 flex items-center gap-1.5">
                                  <Ban className="h-3 w-3" />Cancelled by HR
                                </div>
                                <div>
                                  <span className="text-muted-foreground">By: </span>
                                  <span className="font-medium">{leave.admin_cancelled_by_name || 'Admin'}</span>
                                </div>
                                <div>
                                  <span className="text-muted-foreground">When: </span>
                                  <span>{formatDate(leave.cancelled_at)}</span>
                                </div>
                                <div className="pt-1 border-t border-border/50">
                                  <div className="text-muted-foreground mb-0.5">Reason</div>
                                  <div className="leading-snug whitespace-normal break-words">
                                    {leave.admin_cancel_reason || '—'}
                                  </div>
                                </div>
                                {leave.balance_restored !== undefined && (
                                  <div className="text-[11px] pt-1 border-t border-border/50">
                                    {leave.balance_restored
                                      ? <span className="text-emerald-500">✓ Balance restored</span>
                                      : <span className="text-zinc-400">Balance NOT restored</span>}
                                  </div>
                                )}
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        ) : (leave.status === 'rejected' && leave.rejection_reason) ? (
                          <TooltipProvider delayDuration={150}>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span className="inline-flex items-center cursor-help" data-testid={`rejected-badge-${leave.id}`}>
                                  <Badge className={statusColors[leave.status]}>{leave.status}</Badge>
                                </span>
                              </TooltipTrigger>
                              <TooltipContent
                                side="top"
                                className="max-w-xs bg-background text-foreground border border-border shadow-lg p-3 space-y-1.5 text-xs"
                                data-testid={`rejected-tooltip-${leave.id}`}
                              >
                                <div className="font-semibold text-red-400 flex items-center gap-1.5">
                                  <X className="h-3 w-3" />Rejection reason
                                </div>
                                <div className="leading-snug whitespace-pre-wrap break-words">
                                  {leave.rejection_reason}
                                </div>
                                {leave.rejected_by_name && (
                                  <div className="text-[11px] pt-1 border-t border-border/50">
                                    <span className="text-muted-foreground">By: </span>
                                    <span className="font-medium">{leave.rejected_by_name}</span>
                                  </div>
                                )}
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        ) : (leave.status === 'approved' && leave.approver_comment) ? (
                          <TooltipProvider delayDuration={150}>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span className="inline-flex items-center cursor-help" data-testid={`approved-badge-${leave.id}`}>
                                  <Badge className={statusColors[leave.status]}>{leave.status}</Badge>
                                </span>
                              </TooltipTrigger>
                              <TooltipContent
                                side="top"
                                className="max-w-xs bg-background text-foreground border border-border shadow-lg p-3 space-y-1.5 text-xs"
                                data-testid={`approved-tooltip-${leave.id}`}
                              >
                                <div className="font-semibold text-emerald-400 flex items-center gap-1.5">
                                  <Check className="h-3 w-3" />Approver comment
                                </div>
                                <div className="leading-snug whitespace-pre-wrap break-words">
                                  {leave.approver_comment}
                                </div>
                                {leave.approved_by_name && (
                                  <div className="text-[11px] pt-1 border-t border-border/50">
                                    <span className="text-muted-foreground">By: </span>
                                    <span className="font-medium">{leave.approved_by_name}</span>
                                  </div>
                                )}
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        ) : (
                          <Badge className={statusColors[leave.status]}>{leave.status}</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">{formatDate(leave.applied_on || leave.created_at)}</TableCell>
                      <TableCell className="text-right">
                        {leave.status === 'pending' && (
                          <div className="flex items-center justify-end gap-1">
                            <Button size="sm" variant="ghost" className="text-emerald-500 h-8" onClick={() => openApproveDialog(leave)} data-testid={`approve-${leave.id}`}>
                              <Check className="h-3.5 w-3.5 mr-1" />Approve
                            </Button>
                            <Button size="sm" variant="ghost" className="text-red-500 h-8" onClick={() => setRejectDialog({ open: true, id: leave.id })} data-testid={`reject-${leave.id}`}>
                              <X className="h-3.5 w-3.5 mr-1" />Reject
                            </Button>
                            <Button size="sm" variant="ghost" className="text-amber-500 h-8" onClick={() => openAdminCancelDialog(leave)} data-testid={`admin-cancel-${leave.id}`}>
                              <Ban className="h-3.5 w-3.5 mr-1" />Cancel
                            </Button>
                          </div>
                        )}
                        {leave.status === 'approved' && (
                          <div className="flex items-center justify-end gap-1">
                            <Button size="sm" variant="ghost" className="text-amber-500 h-8" onClick={() => openAdminCancelDialog(leave)} data-testid={`admin-cancel-${leave.id}`}>
                              <Ban className="h-3.5 w-3.5 mr-1" />Cancel
                            </Button>
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          )}
          <Pagination
            page={page}
            total={totalRequests}
            pageSize={pageSize}
            onPageChange={setPage}
            testIdPrefix="leaves-pagination"
          />
        </TabsContent>

        <TabsContent value="balances">
          <Card>
            <CardHeader><CardTitle className="text-lg">Employee Leave Balances (Current FY)</CardTitle></CardHeader>
            <Table>
              <TableHeader><TableRow>
                <TableHead>Employee</TableHead>
                <TableHead>Department</TableHead>
                <TableHead className="text-right">Carry Forward</TableHead>
                <TableHead className="text-right">Accrued</TableHead>
                <TableHead className="text-right">Used</TableHead>
                <TableHead className="text-right">Balance</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {balances.map(b => (
                  <TableRow key={b.employee_id} data-testid={`bal-${b.employee_id}`}>
                    <TableCell><div className="flex items-center gap-2"><PersonAvatar firstName={b.first_name || (b.employee_name || '').split(' ')[0]} lastName={b.last_name || (b.employee_name || '').split(' ').slice(1).join(' ')} picture={b.profile_picture} seed={b.employee_id} size="sm" /><span className="font-medium">{b.employee_name}</span></div></TableCell>
                    <TableCell className="text-sm text-muted-foreground">{b.department}</TableCell>
                    <TableCell className="text-right">{b.carry_forward}</TableCell>
                    <TableCell className="text-right">{b.total_accrued}</TableCell>
                    <TableCell className="text-right">{b.total_used}</TableCell>
                    <TableCell className="text-right font-semibold">
                      <span className={b.current_balance < 0 ? 'text-red-400' : 'text-emerald-500'}>{b.current_balance}</span>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        <TabsContent value="audit">
          <Card>
            <CardHeader><CardTitle className="text-lg flex items-center gap-2"><History className="h-5 w-5" />Leave Audit Trail</CardTitle></CardHeader>
            <Table>
              <TableHeader><TableRow>
                <TableHead>Timestamp</TableHead>
                <TableHead>Action</TableHead>
                <TableHead>Value</TableHead>
                <TableHead>Details</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {auditLog.map(log => (
                  <TableRow key={log.id}>
                    <TableCell className="text-sm">{new Date(log.timestamp).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short', hour12: true })}</TableCell>
                    <TableCell><Badge variant="outline" className="text-xs">{log.action_type}</Badge></TableCell>
                    <TableCell className="font-mono text-sm">{log.value > 0 ? '+' : ''}{log.value}</TableCell>
                    <TableCell className="text-sm text-muted-foreground max-w-[300px] truncate">{log.details}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
          <Pagination
            page={auditPage}
            total={auditTotal}
            pageSize={pageSize}
            onPageChange={setAuditPage}
            testIdPrefix="leaves-audit-pagination"
          />
        </TabsContent>
      </Tabs>

      {/* Reject Dialog */}
      <Dialog open={approveDialog.open} onOpenChange={o => !o && setApproveDialog({ open: false, leave: null, balance: null })}>
        <DialogContent className="max-w-md" data-testid="approve-leave-dialog">
          <DialogHeader>
            <DialogTitle>Approve Leave</DialogTitle>
            <DialogDescription>
              Approve {approveDialog.leave?.days} day(s) for <strong>{approveDialog.leave?.employee_name}</strong>.
              Split the request into paid + unpaid days as needed.
            </DialogDescription>
          </DialogHeader>

          {approveDialog.balance && (
            <div
              className="rounded-md border border-blue-500/20 bg-blue-500/5 p-3 text-xs"
              data-testid="approve-balance-card"
            >
              <div className="flex items-center justify-between mb-2">
                <span className="font-semibold text-blue-500">
                  Accrued Leave Balance{approveDialog.balance.fy ? ` · FY ${approveDialog.balance.fy}` : ''}
                </span>
                <span
                  className={
                    approveDialog.balance.available <= 0
                      ? 'text-red-500 font-semibold'
                      : 'text-emerald-500 font-semibold'
                  }
                  data-testid="approve-balance-available"
                >
                  Available: {approveDialog.balance.available} day(s)
                </span>
              </div>
              <div className="grid grid-cols-3 gap-2 text-muted-foreground">
                <div><span className="block text-[10px] uppercase tracking-wide">Accrued</span>{approveDialog.balance.accrued}</div>
                <div><span className="block text-[10px] uppercase tracking-wide">Carry Fwd</span>{approveDialog.balance.carry_forward}</div>
                <div><span className="block text-[10px] uppercase tracking-wide">Used</span>{approveDialog.balance.used}</div>
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3 py-2">
            <div className="space-y-1">
              <Label>Paid Days</Label>
              <Input
                type="number" step="0.5" min="0"
                value={approvePaid}
                onChange={e => setApprovePaid(e.target.value)}
                data-testid="approve-paid-days"
              />
              <p className="text-[11px] text-muted-foreground">Deducted from accrued balance</p>
            </div>
            <div className="space-y-1">
              <Label>Unpaid Days</Label>
              <Input
                type="number" step="0.5" min="0"
                value={approveUnpaid}
                onChange={e => setApproveUnpaid(e.target.value)}
                data-testid="approve-unpaid-days"
              />
              <p className="text-[11px] text-muted-foreground">Salary will be deducted for these days</p>
            </div>
            <div className="col-span-2 space-y-1">
              <Label>Comment (optional)</Label>
              <Textarea
                rows={2}
                value={approveComment}
                onChange={e => setApproveComment(e.target.value)}
                placeholder="Visible to the employee in the approval email"
                data-testid="approve-comment"
              />
            </div>
            <div className="col-span-2 text-xs">
              Total: <span className={
                Math.abs((parseFloat(approvePaid)||0) + (parseFloat(approveUnpaid)||0) - (Number(approveDialog.leave?.days)||0)) > 0.01
                  ? 'text-red-500 font-semibold'
                  : 'text-emerald-500 font-semibold'
              }>
                {(parseFloat(approvePaid)||0) + (parseFloat(approveUnpaid)||0)} / {approveDialog.leave?.days || 0} days
              </span>
            </div>
          </div>
          <div className="rounded-md border border-blue-500/20 bg-blue-500/10 px-3 py-2 text-xs text-blue-300 flex items-start gap-2" data-testid="approve-email-note">
            <Mail className="h-4 w-4 mt-0.5 shrink-0" />
            <span>The employee will receive an email confirming the approval (with the comment above, if provided).</span>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setApproveDialog({ open: false, leave: null, balance: null })}>Cancel</Button>
            <Button onClick={handleApprove} data-testid="approve-confirm-btn">Approve</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={rejectDialog.open} onOpenChange={o => !o && setRejectDialog({ open: false, id: null })}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Reject Leave</DialogTitle><DialogDescription>Provide a reason for rejection</DialogDescription></DialogHeader>
          <Textarea value={rejectReason} onChange={e => setRejectReason(e.target.value)} placeholder="Reason for rejection..." rows={3} data-testid="reject-reason" />
          <div className="rounded-md border border-blue-500/20 bg-blue-500/10 px-3 py-2 text-xs text-blue-300 flex items-start gap-2" data-testid="reject-email-note">
            <Mail className="h-4 w-4 mt-0.5 shrink-0" />
            <span>The employee will receive an email with the rejection reason above.</span>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectDialog({ open: false, id: null })}>Cancel</Button>
            <Button variant="destructive" onClick={handleReject} data-testid="confirm-reject"><X className="h-4 w-4 mr-2" />Reject</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Admin / HR Cancel Dialog */}
      <Dialog open={adminCancelDialog.open} onOpenChange={o => !o && setAdminCancelDialog({ open: false, leave: null })}>
        <DialogContent className="max-w-md" data-testid="admin-cancel-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-500">
              <Ban className="h-5 w-5" />Cancel Leave (HR Override)
            </DialogTitle>
            <DialogDescription>
              Cancel {adminCancelDialog.leave?.days} day(s) for <strong>{adminCancelDialog.leave?.employee_name}</strong>
              {adminCancelDialog.leave?.status === 'approved'
                ? ' — paid days will be returned to their balance by default.'
                : '.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-1">
              <Label htmlFor="admin-cancel-reason">Reason <span className="text-red-500">*</span></Label>
              <Textarea
                id="admin-cancel-reason"
                rows={3}
                value={adminCancelReason}
                onChange={e => setAdminCancelReason(e.target.value)}
                placeholder="e.g. Employee resigned; voiding remaining requests. (min 10 chars)"
                data-testid="admin-cancel-reason"
              />
              <p className="text-[11px] text-muted-foreground">
                Recorded in the audit log. The employee receives an email only if &ldquo;Notify employee&rdquo; below is turned on.
              </p>
            </div>
            {adminCancelDialog.leave?.status === 'approved' && (
              <div className="flex items-start justify-between gap-3 rounded-md border border-amber-500/20 bg-amber-500/5 p-3">
                <div>
                  <Label className="text-sm">Restore balance</Label>
                  <p className="text-[11px] text-muted-foreground mt-0.5">
                    Turn off only if the employee actually took the leave but it was logged twice or against the wrong person.
                  </p>
                </div>
                <Switch
                  checked={adminCancelRestore}
                  onCheckedChange={setAdminCancelRestore}
                  data-testid="admin-cancel-restore-toggle"
                />
              </div>
            )}
            <div className="flex items-start justify-between gap-3 rounded-md border border-blue-500/20 bg-blue-500/5 p-3" data-testid="admin-cancel-notify-row">
              <div>
                <Label className="text-sm flex items-center gap-1.5">
                  <Mail className="h-3.5 w-3.5" />Notify employee
                </Label>
                <p className="text-[11px] text-muted-foreground mt-0.5">
                  {adminCancelNotify
                    ? 'A cancellation email with the reason above will be sent to the employee.'
                    : 'Default: silent. Turn ON only when the employee should know (e.g. courtesy notice after a mistaken booking).'}
                </p>
              </div>
              <Switch
                checked={adminCancelNotify}
                onCheckedChange={setAdminCancelNotify}
                data-testid="admin-cancel-notify-toggle"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAdminCancelDialog({ open: false, leave: null })}>Back</Button>
            <Button
              variant="destructive"
              onClick={handleAdminCancel}
              disabled={adminCancelBusy || adminCancelReason.trim().length < 10}
              data-testid="confirm-admin-cancel"
            >
              {adminCancelBusy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Ban className="h-4 w-4 mr-2" />}
              Cancel Leave
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Accrual Dialog */}
      <Dialog open={accrualDialog} onOpenChange={setAccrualDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Play className="h-5 w-5 text-primary" />Run Leave Accrual</DialogTitle>
            <DialogDescription>Recompute earned leave for every active employee from their joining date through today using daily-prorata. Carry-forward at FY end is capped at 45 days.</DialogDescription>
          </DialogHeader>
          <div className="p-3 rounded-lg bg-blue-500/5 border border-blue-500/20 text-sm text-muted-foreground">
            <AlertTriangle className="h-4 w-4 inline mr-1 text-blue-500" />
            Fully idempotent — safe to run any time. Also runs automatically once per day on the first login.
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAccrualDialog(false)}>Cancel</Button>
            <Button onClick={handleRunAccrual} disabled={accrualLoading} data-testid="confirm-accrual">
              {accrualLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Play className="h-4 w-4 mr-2" />}Run Now
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
